// src/app/constants/models.ts
//
// Single source of truth for AI model/provider configuration.
// Mirrors common/constants/models.py on the backend — keep in lock-step.
//
// CONTRACT WITH THE BACKEND
// ─────────────────────────
// The `id` field is the wire identifier sent in API/WebSocket payloads.
// It is also persisted in DB rows and analytics events.
// ⚠️ NEVER rename an `id` once shipped. Rename `label` / `modelFamily`
// freely, but `id` is forever.
//
// To hide a model from pickers     → set `inPicker: false`
// To deprecate a model gracefully  → set `runnable: false` (picker shows
//                                    "Coming soon" badge, backend rejects)
// To rename what users see         → edit `label` / `modelFamily`
// NEVER delete a row — flip `runnable` to false instead.

// ─── Types ────────────────────────────────────────────────────────────────

export type ProviderId =
  | "openai"
  | "gptweb"
  | "claude"
  | "perplexity"
  | "gemini"
  | "aimode"
  | "copilot"
  | "llama";

export type ModelAvailability = "live" | "upcoming";

export type MeasurementModel = Extract<ProviderId, "openai" | "gptweb" | "claude" | "perplexity" | "gemini" | "aimode">;

export interface ModelCatalogItem {
  /** ⚠️ DO NOT EDIT — wire identifier, persisted to DB. */
  id: ProviderId;
  label: string;
  fullLabel: string;
  provider: string;
  description: string;
  recommended: boolean;
  availability: ModelAvailability;
  inPicker: boolean;
  /**
   * Whether the BACKEND actually accepts this model in run endpoints.
   * When false, the picker shows the model but disables it with a
   * "Coming soon" badge — selecting it would be rejected server-side.
   * Mirrors `runnable` in common/constants/models.py.
   */
  runnable: boolean;
  /**
   * Measurement-only models (e.g. the web-grounded `gptweb` variant) set this
   * true to stay OUT of the brand-monitoring / analytics default model set
   * (`DEFAULT_LLM_MODELS`) and cross-model headline aggregates. The onboarding
   * measurement battery routes them explicitly, so this does not affect
   * measurement. Mirrors `excluded_from_analytics` in common/constants/models.py.
   */
  excludedFromAnalytics?: boolean;
  /**
   * Is this lane CHECKED BY DEFAULT when the user has made no explicit choice?
   * Separate from `recommended`, which says whether the lane is OFFERED at all.
   * `recommended && live` defines the offered set (MEASUREMENT_MODELS, what the
   * Stage 4 picker renders); this flag alone defines the default-on set
   * (MEASUREMENT_DEFAULT_MODELS). Undefined means default-on, so existing lanes
   * are unaffected. Mirrors `default_selected` in common/constants/models.py.
   */
  defaultSelected?: boolean;
  color: string;
  perCallCostUsd: number;
  modelFamily?: string;
  icon?: string;
  aliases?: string[];
}

// ─── Catalog ──────────────────────────────────────────────────────────────
//
// ⚠️ STABLE WIRE CONTRACT: every `id` below is sent to the backend and
// stored in DB rows. NEVER rename an `id`. To deprecate, set
// `runnable: false` and add a deprecation note — do not delete the row.

export const MODEL_CATALOG: ModelCatalogItem[] = [
  {
    id: "openai", // ⚠️ DO NOT EDIT
    label: "ChatGPT",
    fullLabel: "ChatGPT",
    provider: "OpenAI",
    description: "Grounded with real-time web search to prevent hallucinations.",
    recommended: true,
    availability: "live",
    inPicker: true,
    runnable: true,
    color: "var(--accent)",
    perCallCostUsd: 0.04,
    modelFamily: "ChatGPT",
    icon: "🤖",
    aliases: ["chatgpt", "gpt", "gpt4", "gpt-4", "gpt-4o", "openai"],
  },
  {
    // "ChatGPT (Web)" is a measurement-only variant: the OpenAI model with
    // live web search ON, tracked separately from the base ChatGPT entry. It
    // is scoped to the onboarding measurement picker only — `inPicker: false`
    // keeps it out of the client model picker (PICKER_MODEL_IDS), and
    // `excludedFromAnalytics: true` keeps it out of the brand-monitoring /
    // analytics default set (DEFAULT_LLM_MODELS). The onboarding measurement
    // battery reads MEASUREMENT_MODELS (recommended && live), so it still
    // routes gptweb. Provider is intentionally NOT "OpenAI": the alias map
    // registers provider.toLowerCase(), so reusing "OpenAI" would clobber the
    // base model's normalization.
    id: "gptweb", // ⚠️ DO NOT EDIT
    label: "ChatGPT (Web)",
    fullLabel: "ChatGPT (Web)",
    provider: "OpenAI (web)",
    description: "ChatGPT with live web search — tracked as a separate web-grounded signal.",
    recommended: true,
    availability: "live",
    inPicker: false,
    runnable: true,
    excludedFromAnalytics: true,
    color: "var(--signal-blue)",
    perCallCostUsd: 0.03,
    modelFamily: "ChatGPT (Web)",
    icon: "🧠",
    aliases: ["gptweb", "gpt-web", "gpt_web", "gpt_base", "openai_base", "gpt-noweb"],
  },
  {
    id: "claude", // ⚠️ DO NOT EDIT
    label: "Claude",
    fullLabel: "Claude",
    provider: "Anthropic",
    description: "Deep analytical reasoning with web-search tool integration.",
    recommended: true,
    availability: "live",
    inPicker: true,
    runnable: true,
    color: "var(--spectral-violet)",
    perCallCostUsd: 0.06,
    modelFamily: "Claude",
    icon: "🟠",
    aliases: ["claude", "anthropic"],
  },
  {
    id: "perplexity", // ⚠️ DO NOT EDIT
    label: "Perplexity",
    fullLabel: "Perplexity",
    provider: "Perplexity",
    description: "Native AI search engine for the most up-to-date citations.",
    recommended: true,
    availability: "live",
    inPicker: true,
    runnable: true,
    color: "var(--infrared-coral)",
    perCallCostUsd: 0.02,
    modelFamily: "Perplexity",
    icon: "🔍",
    aliases: ["perplexity", "perplex", "sonar"],
  },
  {
    id: "gemini", // ⚠️ DO NOT EDIT
    label: "Gemini",
    fullLabel: "Gemini (Google)",
    provider: "Google",
    description: "Fast, high-window context for broad data gathering.",
    recommended: true,
    availability: "live",
    inPicker: true,
    runnable: true,
    color: "var(--chart-2)",
    perCallCostUsd: 0.03,
    modelFamily: "Gemini",
    icon: "🟢",
    aliases: ["gemini", "google", "bard"],
  },
  {
    // Google AI Mode is a scraped search surface, not an LLM. The backend
    // fetches it via DataForSEO and routes it in call_llm like gptweb. It is
    // a FIRST-CLASS blended lane (LOCKED decision A, see
    // SPEC_aimode_measurement_lane.md section 7): excludedFromAnalytics is
    // deliberately absent/false, so WHEN SELECTED it blends into analytics like
    // any other surface. That is exactly why it is not default-on: see
    // defaultSelected below. Not version-pinnable, so it has no entry in
    // MEASUREMENT_LANE_OPTIONS below; the pinned request conditions recorded
    // per call are the version-pin analog. Provider is intentionally NOT
    // "Google": the alias map registers provider.toLowerCase(), so reusing
    // "Google" would clobber the gemini normalization. Mirrors
    // common/constants/models.py; keep in lock-step.
    id: "aimode", // ⚠️ DO NOT EDIT
    label: "Google AI Mode",
    fullLabel: "Google AI Mode",
    provider: "Google (AI Mode)",
    description: "Google's AI Mode search answer, scored from the live SERP payload.",
    recommended: true,
    availability: "live",
    inPicker: false,
    runnable: true,
    // OFFERED, NOT DEFAULT-ON. Mirrors default_selected=False in the backend
    // catalog. A paid scraped surface that blends into the headline rates must
    // be elected per run, not switched on for every customer by a deploy: until
    // run-over-run coverage change is detectable, a lane appearing mid-history
    // moves the blended numbers in a way nothing distinguishes from real
    // movement. This is a LAUNCH GATE, not a reversal of decision A: Swen
    // ruled 2026-07-30 that coverage expansion on a client surface is an
    // announced re-baseline made per customer, and policy A (default-on
    // everywhere) is the end state we graduate to that way.
    defaultSelected: false,
    // Swen-picked 2026-07-18: Google green (official brand hue). Deliberate
    // literal hex; no theme token carries this color, and the only other green
    // lane (Copilot, var(--chart-3)) is upcoming and never renders.
    color: "#34A853",
    // MEASURED 2026-07-30, not listed: the credentialed smoke run returned
    // $0.004 per Live Advanced call, double what the vendor's pricing page
    // implied. Was 0.0012 here, which drifted from the backend catalog after
    // the live correction landed there. Keep both catalogs in lock-step.
    perCallCostUsd: 0.004,
    modelFamily: "Google AI Mode",
    icon: "✨",
    aliases: ["aimode", "ai-mode", "google-ai-mode", "gaim"],
  },
  {
    id: "copilot", // ⚠️ DO NOT EDIT
    label: "Copilot",
    fullLabel: "Copilot (Microsoft)",
    provider: "Microsoft",
    description: "Deep Microsoft ecosystem + enterprise context",
    recommended: false,
    availability: "upcoming",
    inPicker: false,
    runnable: false,
    color: "var(--chart-3)",
    perCallCostUsd: 0.04,
    modelFamily: "Copilot",
    icon: "🔷",
    aliases: ["copilot", "microsoft"],
  },
  {
    id: "llama", // ⚠️ DO NOT EDIT
    label: "Llama",
    fullLabel: "Llama",
    provider: "Meta",
    description: "Open source alternative with solid performance",
    recommended: false,
    availability: "upcoming",
    inPicker: false,
    runnable: false,
    color: "var(--chart-4)",
    perCallCostUsd: 0.01,
    modelFamily: "Llama",
    icon: "🦙",
    aliases: ["llama", "meta"],
  },
];

// ─── Derived collections ──────────────────────────────────────────────────

export const LIVE_MODEL_IDS: ProviderId[] = MODEL_CATALOG
  .filter((m) => m.availability === "live")
  .map((m) => m.id);

export const UPCOMING_MODEL_IDS: ProviderId[] = MODEL_CATALOG
  .filter((m) => m.availability === "upcoming")
  .map((m) => m.id);

export const PICKER_MODEL_IDS: ProviderId[] = MODEL_CATALOG
  .filter((m) => m.inPicker)
  .map((m) => m.id);

export const RUNNABLE_MODEL_IDS: ProviderId[] = MODEL_CATALOG
  .filter((m) => m.runnable)
  .map((m) => m.id);

// The DEFAULT model set for brand monitoring / analytics, and the default
// selection for the client model picker (UpmodeHome) and the antenna /
// monitoring run. Two independent flags narrow it, and they answer different
// questions. `excludedFromAnalytics` answers "may this lane appear in blended
// analytics at all", which is what fences off measurement-only variants like
// gptweb. `defaultSelected` answers "is it picked for the user when they did
// not choose", which is what gates aimode: a first-class blended surface can
// still be opt-in. Mirrors backend DEFAULT_LLM_MODELS; keep in lock-step.
export const DEFAULT_LLM_MODELS: ProviderId[] = MODEL_CATALOG
  .filter(
    (m) =>
      m.availability === "live" &&
      !m.excludedFromAnalytics &&
      m.defaultSelected !== false
  )
  .map((m) => m.id);

// ─── Picker-ready shape ───────────────────────────────────────────────────

export interface AIModel {
  id: ProviderId;
  name: string;
  provider: string;
  description: string;
  recommended: boolean;
  isOptional?: boolean;
  /**
   * When true, the picker should disable this entry (greyed out,
   * "Coming soon" badge). Driven by `runnable` on the catalog entry.
   */
  comingSoon?: boolean;
}

export const AVAILABLE_AI_MODELS: AIModel[] = MODEL_CATALOG
  .filter((m) => m.inPicker)
  .map((m) => ({
    id: m.id,
    name: m.modelFamily ?? m.label,
    provider: m.provider,
    description: m.description,
    recommended: m.recommended,
    isOptional: m.availability !== "live" ? true : undefined,
    comingSoon: !m.runnable ? true : undefined,
  }));

// Derived from the catalog: every model that is both recommended and live.
// Stage 4 (measurement run) and any onboarding tooling read this — to add /
// remove a measurement model, flip its `recommended` flag in MODEL_CATALOG
// rather than editing this list. Mirrors backend MEASUREMENT_MODELS.
export const MEASUREMENT_MODELS: MeasurementModel[] = MODEL_CATALOG
  .filter((m) => m.recommended && m.availability === "live")
  .map((m) => m.id as MeasurementModel);

// Derived from the catalog: the lanes CHECKED BY DEFAULT when the user has not
// chosen. A strict subset of MEASUREMENT_MODELS, never a replacement for it.
// Render MEASUREMENT_MODELS (what may be selected); seed initial state and
// fallbacks from this. Mirrors backend MEASUREMENT_DEFAULT_MODELS.
export const MEASUREMENT_DEFAULT_MODELS: MeasurementModel[] = MODEL_CATALOG
  .filter((m) => m.recommended && m.availability === "live" && m.defaultSelected !== false)
  .map((m) => m.id as MeasurementModel);

export const PROVIDER_LABEL: Record<ProviderId, string> = MODEL_CATALOG.reduce(
  (acc, m) => { acc[m.id] = m.label; return acc; },
  {} as Record<ProviderId, string>,
);

export const PROVIDER_FULL_LABEL: Record<ProviderId, string> = MODEL_CATALOG.reduce(
  (acc, m) => { acc[m.id] = m.fullLabel; return acc; },
  {} as Record<ProviderId, string>,
);

const ALIAS_TO_ID: Record<string, ProviderId> = (() => {
  const map: Record<string, ProviderId> = {};
  for (const model of MODEL_CATALOG) {
    map[model.id] = model.id;
    map[model.label.toLowerCase()] = model.id;
    map[model.provider.toLowerCase()] = model.id;
    for (const alias of model.aliases ?? []) {
      map[alias.toLowerCase()] = model.id;
    }
  }
  return map;
})();

// ─── Helper functions ─────────────────────────────────────────────────────

export function normalizeProviderKey(input: string | null | undefined): string {
  const v = (input || "").trim().toLowerCase();
  if (!v) return v;

  if (ALIAS_TO_ID[v]) return ALIAS_TO_ID[v];

  for (const [alias, id] of Object.entries(ALIAS_TO_ID)) {
    if (alias.length >= 4 && v.includes(alias)) return id;
  }

  return v;
}

export function isLiveModel(id: string): boolean {
  const norm = normalizeProviderKey(id);
  return LIVE_MODEL_IDS.includes(norm as ProviderId);
}

/**
 * Whether the backend will currently accept this model in a run endpoint.
 * UI should pre-filter selectedModels through this before submitting to
 * avoid an avoidable error round-trip.
 */
export function isRunnableModel(id: string): boolean {
  const norm = normalizeProviderKey(id);
  return RUNNABLE_MODEL_IDS.includes(norm as ProviderId);
}

export function getModelById(id: string | null | undefined): ModelCatalogItem | undefined {
  const norm = normalizeProviderKey(id || "");
  return MODEL_CATALOG.find((m) => m.id === norm);
}

export function getModelLabel(id: string, fallback?: string): string {
  const m = getModelById(id);
  if (m) return m.label;
  if (fallback) return fallback;
  return id ? id.charAt(0).toUpperCase() + id.slice(1) : "Unknown";
}

export function getModelDisplayName(id: string, fallback?: string): string {
  const m = getModelById(id);
  if (m) return m.modelFamily ?? m.label;
  if (fallback) return fallback;
  return id ? id.charAt(0).toUpperCase() + id.slice(1) : "Unknown";
}

export function getModelFullLabel(id: string, fallback?: string): string {
  const m = getModelById(id);
  if (m) return m.fullLabel;
  if (fallback) return fallback;
  return id ? id.charAt(0).toUpperCase() + id.slice(1) : "Unknown";
}

export function getProviderLabel(id: string, fallback?: string): string {
  const m = getModelById(id);
  if (m) return m.provider;
  return fallback ?? (id ? id.charAt(0).toUpperCase() + id.slice(1) : "Unknown");
}

export function getModelColor(id: string): string {
  const m = getModelById(id);
  return m?.color ?? "var(--muted-foreground)";
}

export function getPerCallCost(id: string): number {
  const m = getModelById(id);
  return m?.perCallCostUsd ?? 0;
}

export function getModelIcon(id: string): string {
  const m = getModelById(id);
  return m?.icon ?? "🔷";
}

export function getModelFamily(id: string): string | undefined {
  return getModelById(id)?.modelFamily;
}

export function getModelDescription(id: string, fallback = ""): string {
  return getModelById(id)?.description ?? fallback;
}

export function isRecommendedModel(id: string): boolean {
  return Boolean(getModelById(id)?.recommended);
}

/**
 * Whether a model is a measurement-only variant fenced out of the
 * brand-monitoring / analytics views (e.g. gptweb). Mirrors the backend
 * `excluded_from_analytics` flag. Use to drop these lanes from client-facing
 * per-model enumerations (scope chips, filter tabs) so a run sourced from the
 * onboarding measurement battery doesn't surface them in the monitoring UI.
 */
export function isExcludedFromAnalytics(id: string): boolean {
  return Boolean(getModelById(id)?.excludedFromAnalytics);
}

// ─── Measurement battery: per-lane selectable model versions ────────────────
//
// Mirrors MEASUREMENT_LANE_MODEL_CHOICES / MEASUREMENT_MODEL_VERSION_LABELS and
// the version-mode defaults in common/constants/models.py — keep in lock-step.
// DISPLAY + SELECTION ONLY: the backend re-validates every override against its
// own allowlist and resolves the actual model that runs, so drift here can
// never cause a disallowed model to run — worst case a stale label.
//
// ⚠️ The Claude lane is Sonnet-only BY DEFAULT — Opus is the scoring engine and
// is not a general query-battery option. The single exception is
// claude-opus-4-7, kept solely to reproduce Sunbit's run-4 Baseline Read
// (customer 2, executed 2026-05-23), whose Claude query lane ran on Opus 4.7
// before the Sonnet-only guard existed. It is opt-in per run; both defaults stay
// on Sonnet. Do NOT add claude-opus-4-8 (the current scorer) here. Mirrors the
// backend allowlist in common/constants/models.py — keep in lock-step.
//
// ⚠️ The aimode lane is deliberately ABSENT: it is a scraped surface with no
// model versions to choose (mirrors UNVERSIONED_MEASUREMENT_LANES on the
// backend). getMeasurementLaneOption("aimode") returns undefined by design.

export type MeasurementVersionMode = "baseline" | "default";

export interface MeasurementVersionChoice {
  value: string;
  label: string;
}

export interface MeasurementLaneOption {
  lane: MeasurementModel;
  label: string;
  choices: MeasurementVersionChoice[];
  /** Effective version per mode when the lane has no explicit override. */
  defaults: Record<MeasurementVersionMode, string | null>;
  /**
   * Catalog default grounding regime for this lane: true = probed web-grounded
   * (search ON), false = static (parametric only). Mirrors the backend catalog
   * `measurement_web_search` field. A run that doesn't override a lane resolves
   * to this. ⚠️ Keep in lock-step with common/constants/models.py: static lanes
   * (openai/claude/gemini) are false; web lanes (gptweb/perplexity) are true.
   */
  webSearchDefault: boolean;
  /**
   * Lanes with more than one call surface (#90 — today: perplexity only,
   * Sonar chat completions vs. the Agent API). Undefined = this lane has only
   * one endpoint and no endpoint control renders for it. Mirrors the backend
   * MEASUREMENT_LANE_ENDPOINT_CHOICES / ModelCatalogItem.default_endpoint.
   */
  endpoints?: {
    default: "sonar" | "agent";
    choicesByEndpoint: Record<"sonar" | "agent", MeasurementVersionChoice[]>;
    /** Default preset used when endpoint="agent" and no override is given —
     * mirrors the backend catalog's agent_preset_default. There is no
     * baseline/current pin for a surface that postdates the baseline, so
     * version_mode is irrelevant once a non-default endpoint is resolved. */
    agentPresetDefault: string;
  };
}

export const MEASUREMENT_LANE_OPTIONS: MeasurementLaneOption[] = [
  {
    lane: "openai",
    label: "ChatGPT",
    // gpt-6-sol (#110, #111): ChatGPT's Chat tab moved to GPT-6 on 2026-10-07,
    // so GPT-5.5 no longer reflects what a ChatGPT user sees and "Current" now
    // resolves gpt-6-sol (#111). It is the closest API model, NOT an exact
    // match — the API id is the September build, while ChatGPT Chat runs the
    // October build. See the caveat on MEASUREMENT_LANE_MODEL_CHOICES in
    // common/constants/models.py. `baseline` is unchanged on purpose: existing
    // client series keep the version they were measured on.
    choices: [
      { value: "gpt-6-sol", label: "GPT-6 Sol" },
      { value: "gpt-5.5", label: "GPT-5.5" },
      { value: "gpt-5.4-2026-03-05", label: "GPT-5.4 (2026-03-05)" },
    ],
    defaults: { baseline: "gpt-5.4-2026-03-05", default: "gpt-6-sol" },
    webSearchDefault: false, // static lane; gptweb is its web-grounded counterpart
  },
  {
    lane: "gptweb",
    label: "ChatGPT (Web)",
    choices: [
      { value: "gpt-6-sol", label: "GPT-6 Sol" },
      { value: "gpt-5.5", label: "GPT-5.5" },
      { value: "gpt-5.4-2026-03-05", label: "GPT-5.4 (2026-03-05)" },
    ],
    defaults: { baseline: "gpt-5.4-2026-03-05", default: "gpt-6-sol" },
    webSearchDefault: true,
  },
  {
    lane: "claude",
    label: "Claude",
    // Sonnet by default; Opus 4.7 is the run-4 baseline-reproduction option only.
    choices: [
      { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
      { value: "claude-opus-4-7", label: "Claude Opus 4.7" },
    ],
    defaults: { baseline: "claude-sonnet-4-6", default: "claude-sonnet-4-6" },
    webSearchDefault: false, // static lane
  },
  {
    lane: "perplexity",
    label: "Perplexity",
    choices: [{ value: "sonar-pro", label: "Perplexity Sonar Pro" }],
    defaults: { baseline: "sonar-pro", default: "sonar-pro" },
    webSearchDefault: true,
    // Sonar chat completions RETIRED 2026-09-27 (#90), so the Agent API is
    // now this lane's default surface (#97); "sonar" stays selectable per-run
    // only so an older run's config still reproduces as recorded.
    endpoints: {
      default: "agent",
      choicesByEndpoint: {
        sonar: [{ value: "sonar-pro", label: "Perplexity Sonar Pro" }],
        agent: [
          { value: "fast", label: "Fast" },
          { value: "low", label: "Low" },
          { value: "medium", label: "Medium" },
          { value: "high", label: "High" },
          { value: "xhigh", label: "Extra High" },
        ],
      },
      // Mirrors backend agent_preset_default. "fast" is Perplexity's mapping
      // for Sonar Pro, the model this lane has run since the baseline ("low"
      // is where the guide maps Sonar Reasoning Pro) — see the note in
      // common/constants/models.py. Keep the two in lock-step.
      // This is the CONTINUITY value for series already on Fast. New clients
      // are pinned to the Global Settings new-client default ("low" unless an
      // admin changed it), which reaches the picker as firstReadDefaults.
      agentPresetDefault: "fast",
    },
  },
  {
    lane: "gemini",
    label: "Gemini",
    // gemini-4-argon and gemini-3.8-flash are selectable ahead of the default
    // flip so the continuity read can be collected on them; 2.5 Pro stays
    // listed because Google project-gated it rather than withdrawing it,
    // leaving it the only way to reproduce a pre-switch Gemini run on the prod
    // key. Argon is the like-for-like Pro-tier successor (what the lane's
    // search model moves to); 3.8 Flash is the Flash-slot comparison only.
    // Keep in lock-step with MEASUREMENT_LANE_MODEL_CHOICES in
    // common/constants/models.py.
    choices: [
      { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
      { value: "gemini-4-argon", label: "Gemini 4 Argon" },
      { value: "gemini-3.8-flash", label: "Gemini 3.8 Flash" },
    ],
    // Both modes still resolve 2.5 Pro: `baseline` mirrors the continuity pin
    // in MEASUREMENT_MODEL_VERSIONS (which does NOT move with the switch — it
    // is what reproduces the already-collected series), `default` mirrors the
    // catalog search_model_name, which moves to gemini-4-argon once the
    // continuity read is accepted. Flip `default` in the same change as the
    // backend catalog.
    defaults: { baseline: "gemini-2.5-pro", default: "gemini-2.5-pro" },
    webSearchDefault: false, // static lane
  },
];

/** Resolved grounding regime for a lane given optional per-lane overrides:
 * true = web-grounded (search ON), false = static. Falls back to the lane's
 * catalog default. Mirrors the backend resolution in
 * _validate_and_normalize_batch_config. */
export function resolveMeasurementWebSearch(
  lane: string,
  overrides?: Record<string, boolean> | null,
): boolean {
  const opt = getMeasurementLaneOption(lane);
  const fallback = opt ? opt.webSearchDefault : true;
  const norm = normalizeProviderKey(lane);
  const o = overrides?.[norm];
  return typeof o === "boolean" ? o : fallback;
}

/** Resolved endpoint for a lane given optional per-lane overrides (#90):
 * "sonar" or "agent". Falls back to the lane's catalog default — "sonar" for
 * every lane without an `endpoints` block. Mirrors the backend resolution in
 * _validate_and_normalize_batch_config / get_measurement_endpoint. */
export function resolveMeasurementEndpoint(
  lane: string,
  overrides?: Record<string, string> | null,
): "sonar" | "agent" {
  const opt = getMeasurementLaneOption(lane);
  const fallback = opt?.endpoints?.default ?? "sonar";
  const norm = normalizeProviderKey(lane);
  const o = overrides?.[norm];
  return o === "agent" || o === "sonar" ? o : fallback;
}

export function getMeasurementLaneOption(
  lane: string,
): MeasurementLaneOption | undefined {
  const norm = normalizeProviderKey(lane);
  return MEASUREMENT_LANE_OPTIONS.find((o) => o.lane === norm);
}

/** The effective model version (or, for a non-default endpoint, preset) a
 * lane will run, given the mode + any overrides. An override only wins if
 * it's one of the lane's allowed choices for the RESOLVED endpoint (#90:
 * matches the backend guard, which validates a model_overrides value against
 * whichever endpoint that lane resolved to); otherwise the version_mode
 * default applies — except on the Agent endpoint, where version_mode is
 * irrelevant and that endpoint's own preset default applies instead (mirrors
 * get_measurement_model_name, which keys on the endpoint being "agent", not on
 * it differing from the lane default: "agent" IS the default now, and the
 * version_mode defaults are Sonar model names). */
export function resolveMeasurementVersion(
  lane: string,
  mode: MeasurementVersionMode,
  overrides?: Record<string, string> | null,
  endpointOverrides?: Record<string, string> | null,
  /** The customer's standing per-lane version pins, from
   * GET /customers/{id}/measurement/version-policy. A client with no baseline
   * of its own is pinned to the versions its FIRST read resolved, so from its
   * second read on the catalog default is no longer what runs — without these
   * the picker would confidently display the wrong model. Pins apply in
   * "default" (Current) mode only, and never over an explicit override, which
   * mirrors the worker's precedence exactly. */
  pins?: Record<string, string> | null,
): string | null {
  const opt = getMeasurementLaneOption(lane);
  if (!opt) return null;
  const override = overrides?.[opt.lane];
  const pin = mode === "default" ? pins?.[opt.lane] : undefined;
  if (opt.endpoints) {
    const endpoint = resolveMeasurementEndpoint(lane, endpointOverrides);
    const choices = opt.endpoints.choicesByEndpoint[endpoint];
    if (override && choices.some((c) => c.value === override)) return override;
    // A pin taken on one call surface says nothing about the other — the Agent
    // API speaks preset tiers, Sonar speaks model names (mirrors the backend's
    // measurement_pin_applies).
    if (pin && choices.some((c) => c.value === pin)) return pin;
    return endpoint === "agent"
      ? opt.endpoints.agentPresetDefault
      : opt.defaults[mode] ?? null;
  }
  if (override && opt.choices.some((c) => c.value === override)) return override;
  if (pin) return pin;
  return opt.defaults[mode] ?? null;
}

export function getMeasurementVersionLabel(
  value: string | null | undefined,
): string {
  const v = (value || "").trim();
  if (!v) return "Auto";
  for (const opt of MEASUREMENT_LANE_OPTIONS) {
    const found = opt.choices.find((c) => c.value === v);
    if (found) return found.label;
    if (opt.endpoints) {
      for (const choices of Object.values(opt.endpoints.choicesByEndpoint)) {
        const endpointMatch = choices.find((c) => c.value === v);
        if (endpointMatch) return endpointMatch.label;
      }
    }
  }
  return v;
}